/**
 * Re-read DB state (scopes + record counts) from the backend and push
 * it into the Zustand store.
 *
 * Called after any operation that mutates scope shape server-side:
 * - Document upload (eager-execute during run_session).
 * - Anwenden finalizing a session.
 * - Rueckgaengig rolling one back.
 *
 * Kept as a plain function (not a hook) so any callback can invoke it
 * without threading React state through the call site.
 */

import type { QueryClient } from "@tanstack/react-query"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type { ScopeListItem } from "../../../types"

export async function refreshDbState(queryClient?: QueryClient): Promise<void> {
  const [scopes, counts] = await Promise.all([
    api.listScopes().catch(() => [] as ScopeListItem[]),
    api.scopeRecordCounts().catch(() => ({}) as Record<string, number>),
  ])
  const {
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
  } = useStore.getState()
  setDbConfigured(scopes.length > 0)
  setTotalRecordCount(Object.values(counts).reduce((sum, n) => sum + n, 0))
  setScopeRecordCounts(counts)
  if (scopes.length > 0) {
    setScopes(scopes)
    const hiddenIds = scopes.filter((s) => s.is_hidden).map((s) => s.id)
    initHiddenScopes(
      scopes.map((s) => s.id),
      counts,
      hiddenIds,
    )
  } else {
    setScopes([])
  }
  if (queryClient) {
    await queryClient.invalidateQueries()
  }
}
